import dayjs from "dayjs"
import { roundToTwoDecimals, CourseID } from "@coast/utils"
import { toGeocode } from "../../../hooks/useGeocodeFromPartialAddress"
import {
  Coordinates,
  CourseType,
  TransientSlot,
  UserBalance,
  ProductPackageWithPrices,
} from "../../../types"
import {
  geocodeFromPartialAddress,
  getPackagesPricingFactory,
} from "../../../utils/locationUtils"
import { STATE_COORDS_MAP } from "../../../utils/regionUtils"
import { hoursToMinutes } from "../../../utils/misc"
import { CouponsState } from "../../CouponsProvider"
import { GQLProductPackageFieldsFragment } from "../../../graphqlTypes"
import { GQLCartItemPackageWithPricesFragment } from "./CartProvider.graphqlTypes"

// 8am.
export const DAILY_COUPON_START_TIME = dayjs().hour(8).startOf("hour")
// Midnight-ish.
export const DAILY_COUPON_END_TIME = dayjs().hour(23).endOf("hour")

export const isInDailyCouponWindow = () => {
  const now = dayjs()
  return now > DAILY_COUPON_START_TIME && now < DAILY_COUPON_END_TIME
}

export const buildPackagePricesFromId = async (
  couponState: CouponsState,
  packageId: string,
  state: string,
  statePackages: GQLProductPackageFieldsFragment[],
  zipcode?: string,
) => {
  let coordinates: Coordinates

  if (zipcode) {
    const geocode = toGeocode(await geocodeFromPartialAddress(zipcode))
    coordinates = { lat: geocode.lat, lng: geocode.lng }
  } else {
    coordinates = STATE_COORDS_MAP[state.toLowerCase()]
  }

  const getPackagesPricing = await getPackagesPricingFactory(
    coordinates,
    couponState,
  )
  const packagePricing = getPackagesPricing(statePackages)
  return {
    packageWithPrice: packagePricing?.packagesWithPrice?.find(
      (p) => p.id === packageId,
    ),
    region: packagePricing?.region,
  }
}

export const toPackageWithPrices = ({
  __typename,
  ...pack
}: GQLCartItemPackageWithPricesFragment): ProductPackageWithPrices => {
  if (pack.lessonPackage) {
    const { __typename: typeNameLessonPackage, ...lessonPackage } =
      pack.lessonPackage
    return {
      ...pack,
      lessonPackage,
      courseId: pack.courseId as CourseID | null,
      courseType: pack.courseType as CourseType | null,
    }
  }

  return {
    ...pack,
    courseId: pack.courseId as CourseID | null,
    courseType: pack.courseType as CourseType | null,
  }
}

export const unApplyCouponToPackage = (
  productPackage: ProductPackageWithPrices,
): ProductPackageWithPrices => {
  const couponDiscountAmount = productPackage.couponDiscountAmount ?? 0
  return {
    ...productPackage,
    couponDiscountAmount: 0,
    didApplyCoupon: false,
    price: productPackage.price + couponDiscountAmount,
  }
}

export const applyCouponToPackage = (
  {
    isCourseCodeValid,
    isLessonCodeValid,
    courseCoupons,
    lessonCoupons,
    roadTestCoupons,
  }: CouponsState,
  productPackage: ProductPackageWithPrices,
  couponCode: string | undefined,
  regionId: string | undefined,
): ProductPackageWithPrices => {
  const isDeOnly = productPackage.hasCourse && !productPackage.hasLessons

  if (productPackage.didApplyCoupon) {
    return productPackage
  }

  let couponDiscountAmount = 0
  let discountAmount = 0
  let didApplyCoupon = false
  let { price } = productPackage
  switch (isDeOnly) {
    case true:
      if (
        couponCode &&
        isCourseCodeValid(couponCode, productPackage.courseId as CourseID)
          .isValid
      ) {
        discountAmount = 0
        couponDiscountAmount = courseCoupons?.[couponCode].discount ?? 0
        didApplyCoupon = true
      } else {
        discountAmount = 0
        didApplyCoupon = false
      }
      break
    case false:
      if (
        couponCode &&
        isLessonCodeValid(couponCode, regionId, productPackage.lessonPackage)
          .isValid
      ) {
        couponDiscountAmount =
          lessonCoupons?.[couponCode]?.discount ??
          roadTestCoupons?.[couponCode]?.discount ??
          0
        discountAmount = productPackage.msrp - productPackage.price
        didApplyCoupon = true
      } else {
        // lesson packages can have a discount amount non from a coupon
        discountAmount = productPackage.discountAmount
        didApplyCoupon = discountAmount !== productPackage.discountAmount
        couponDiscountAmount = 0
      }
      break
    default:
      break
  }

  price = Math.max(
    0,
    roundToTwoDecimals(
      productPackage.msrp - discountAmount - couponDiscountAmount,
    ),
  )

  return {
    ...productPackage,
    price,
    discountAmount,
    didApplyCoupon,
    couponDiscountAmount,
  }
}

export const maybeAddCourseIdToPackage = (
  productPackage: ProductPackageWithPrices,
  courseId?: CourseID,
): ProductPackageWithPrices => {
  if (!productPackage.hasCourse || !courseId) return productPackage

  return {
    ...productPackage,
    courseId,
  }
}

export enum ValidationErrors {
  AGE_REQUIREMENT_NOT_MET = "AGE_REQUIREMENT_NOT_MET",
  STATE_MISMATCH = "STATE_MISMATCH",
  ALREADY_ENROLLED = "ALREADY_ENROLLED",
  PACKAGE_NOT_ENOUGH_CREDIT = "PACKAGE_NOT_ENOUGH_CREDIT",
  MISSING_LICENSE_ISSUE_DATE = "MISSING_LICENSE_ISSUE_DATE",
  LICENSE_ISSUE_DATE_TOO_RECENT = "LICENSE_ISSUE_DATE_TOO_RECENT",
  MINIMUM_DRIVING_HOURS_NOT_MET = "MINIMUM_DRIVING_HOURS_NOT_MET",
}

export const getColoradoRoadTestValidationError = (
  licenseIssueDate: string | null,
  studentMinutes: UserBalance | null,
  transientLessonSlot: TransientSlot,
) => {
  if (!licenseIssueDate) {
    return ValidationErrors.MISSING_LICENSE_ISSUE_DATE
  }
  if (
    dayjs(licenseIssueDate).isAfter(
      dayjs(transientLessonSlot.startTime).subtract(1, "year"),
    )
  ) {
    return ValidationErrors.LICENSE_ISSUE_DATE_TOO_RECENT
  }
  if ((studentMinutes?.drivingMinutesUsed || 0) < hoursToMinutes(6)) {
    return ValidationErrors.MINIMUM_DRIVING_HOURS_NOT_MET
  }
  return null
}

export function mapItemFieldsToEnums(item: ProductPackageWithPrices) {
  return {
    ...item,
    courseId: (item.courseId as CourseID) ?? null,
    courseType: (item.courseType as CourseType) ?? null,
  }
}
